import type { AuctionPlayer, AuctionResourceType } from '@/pages/auction/AuctionPage';
import { AUCTION_RESOURCES } from '@/pages/auction/AuctionPage';
import type { Resources } from '@/types';
import { usePlayerIcons } from '@/hooks/usePlayerIcons';
import './AuctionPlayerPanel.css';

interface AuctionPlayerPanelProps {
  players: AuctionPlayer[];
  currentPlayerId: string;
  currentResource: AuctionResourceType;
}

// Player colors map
const PLAYER_COLORS: Record<string, string> = {
  red: '#FF4444',
  blue: '#4488FF',
  green: '#44FF44',
  yellow: '#FFFF44',
  purple: '#AA44FF',
  orange: '#FF8844',
  cyan: '#44FFFF',
  pink: '#FF44AA',
};

export default function AuctionPlayerPanel({
  players,
  currentPlayerId,
  currentResource,
}: AuctionPlayerPanelProps) {
  const resourceConfig = AUCTION_RESOURCES.find(r => r.id === currentResource);
  const playerIcons = usePlayerIcons();
  
  // Sort players: current player first, then by player number
  const sortedPlayers = [...players].sort((a, b) => {
    if (a.playerId === currentPlayerId) return -1;
    if (b.playerId === currentPlayerId) return 1;
    return a.playerNumber - b.playerNumber;
  });
  
  return (
    <div className="auction-player-panel">
      <h3 className="panel-title">PLAYERS</h3>
      
      <div className="player-list">
        {sortedPlayers.map(player => {
          const isCurrentPlayer = player.playerId === currentPlayerId;
          const playerColor = PLAYER_COLORS[player.color] || PLAYER_COLORS.red;
          const resourceAmount = player.resources?.[currentResource as keyof Resources] || 0;
          const playerIcon = playerIcons[player.playerNumber];
          
          // Determine status based on resources
          let status = 'neutral';
          if (resourceAmount <= 2) status = 'critical';
          else if (resourceAmount <= 5) status = 'low';
          else if (resourceAmount >= 15) status = 'surplus';
          
          return (
            <div 
              key={player.playerId}
              className={`player-card ${isCurrentPlayer ? 'is-current' : ''} ${player.isAI ? 'is-ai' : ''}`}
              style={{ '--player-color': playerColor } as React.CSSProperties}
            >
              {/* Avatar - Using actual player icons */}
              <div className="player-avatar">
                {playerIcon ? (
                  <img 
                    src={playerIcon} 
                    alt={`Player ${player.playerNumber}`}
                    className="avatar-icon"
                  />
                ) : (
                  <div className="avatar-placeholder">P{player.playerNumber}</div>
                )}
                {player.isAI && <span className="ai-badge">AI</span>}
              </div>
              
              {/* Player Info */}
              <div className="player-info">
                <div className="player-name">
                  {isCurrentPlayer && <span className="you-badge">YOU</span>}
                  {player.username}
                </div>
                
                <div className="player-money">
                  💰 ${player.money}
                </div>
                
                <div className={`player-resource-status status-${status}`}>
                  <span className="resource-icon">{resourceConfig?.emoji}</span>
                  <span className="resource-count">{resourceAmount}</span>
                  <span className="status-label">
                    {status === 'critical' ? 'CRITICAL!' : 
                     status === 'low' ? 'Low' : 
                     status === 'surplus' ? 'Surplus' : ''}
                  </span>
                </div>
              </div>
              
              {/* Role indicator */}
              <div className={`player-role role-${player.role}`}>
                {player.role === 'seller' ? '📤' : player.role === 'buyer' ? '📥' : '👀'}
              </div>
              
              {/* Current price */}
              <div className="player-price">
                ${Math.round(player.currentPrice)}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Resource Summary */}
      <div className="resource-summary">
        <h4>All Resources</h4>
        <div className="resource-grid">
          {AUCTION_RESOURCES.map(resource => {
            const currentPlayer = players.find(p => p.playerId === currentPlayerId);
            const amount = currentPlayer?.resources?.[resource.id as keyof Resources] || 0;
            const isActive = resource.id === currentResource;
            
            return (
              <div 
                key={resource.id}
                className={`resource-item ${isActive ? 'active' : ''}`}
              >
                <span className="resource-emoji">{resource.emoji}</span>
                <span className="resource-amount">{amount}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
